// src/pages/HomeLanding.jsx
import React, { lazy, Suspense } from 'react';
import NavbarLanding from '../components/home/NavbarLanding';
import HeroSection from '../components/home/HeroSection';
import { CalendarDays, BarChart, Paintbrush, Sparkles } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// Todo lo que NO está above-the-fold se lazy-carga para liberar el LCP.
// ShowcaseSection arrastra @headlessui + embla-carousel; mantenerlo lazy es clave.
const LazyShowcaseSection    = lazy(() => import('../components/home/ShowcaseSection'));
const LazyFooter             = lazy(() => import('../components/home/Footer'));
const LazyFeaturesSection    = lazy(() => import('../components/home/FeatureSection'));
const LazyHowItWorksSection  = lazy(() => import('../components/home/HowItWorksSection'));
const LazyPricingSection     = lazy(() => import('../components/home/PricingSection'));
const LazyTestimonialsSection = lazy(() => import('../components/home/TestimonialsSection'));
const LazyFinalCTASection    = lazy(() => import('../components/home/FinalCTASection'));


// Datos para la primera sección (Personalización)
const brandingData = {
    subtitle: "Personalización Profesional",
    title: "Tu Agenda, Tu Estilo",
    description: "Ofrece a tus clientes una experiencia de reserva única y coherente con tu identidad de marca. Haz que tu página de reservas se sienta verdaderamente tuya.",
    features: [
        { name: "Paletas de Colores.", description: "Elige entre nuestras paletas prediseñadas para darle un toque único a tu página, disponible para todos los planes.", icon: Paintbrush },
        { name: "Tu Marca, Tus Colores.", description: "Con el plan Premium, personaliza los colores para que coincidan perfectamente con tu identidad de marca.", icon: Sparkles }
    ],
    images: [
        "/agenda-personalizada-1.png",
        "/agenda-personalizada-2.png",
        "/agenda-personalizada-3.png",
    ],
    imagePosition: 'right' // La imagen estará a la derecha
};

// Datos para la segunda sección (Estadísticas)
const statsData = {
    subtitle: "Análisis Inteligente",
    title: "Decisiones Basadas en Datos",
    description: "Entiende el rendimiento de tu negocio con estadísticas claras y accionables. Identifica tus horarios pico, clientes más leales y mucho más.",
    features: [
        { name: "Horarios Pico.", description: "Descubre qué días y horas son los más solicitados para optimizar tu agenda.", icon: CalendarDays },
        { name: "Rendimiento Mensual.", description: "Visualiza la evolución de tus reservas y detecta tendencias para planificar a futuro.", icon: BarChart }
    ],
    images: [
        "/imagen-stats-1.png", // <-- CAMBIA ESTAS IMÁGENES
        "/imagen-stats-2.png",
        "/imagen-stats-3.png",
        "/imagen-stats-4.png",
    ],
    imagePosition: 'left' // La imagen estará a la izquierda para variar
};
const HomeLanding = () => {

    return (
        <>
            <Helmet>
                <title>AgendateHoy | Agenda online 24/7 para profesionales</title>
                <meta name="description" content="Agenda online 24/7 para profesionales en Uruguay: recibí reservas automáticas, recordatorios por WhatsApp y cobros por Mercado Pago. Plan Básico gratis. Sin tarjeta." />
                <link rel="canonical" href="https://www.agendatehoy.com/" />
                <script type="application/ld+json">{JSON.stringify({
                    "@context": "https://schema.org",
                    "@graph": [
                        {
                            "@type": "Organization",
                            "@id": "https://www.agendatehoy.com/#organization",
                            "name": "AgendateHoy",
                            "url": "https://www.agendatehoy.com/",
                            "logo": "https://www.agendatehoy.com/AgendateHoy-logo-light.png",
                            "sameAs": ["https://instagram.com/agendatehoy"]
                        },
                        {
                            "@type": "WebSite",
                            "@id": "https://www.agendatehoy.com/#website",
                            "url": "https://www.agendatehoy.com/",
                            "name": "AgendateHoy",
                            "publisher": { "@id": "https://www.agendatehoy.com/#organization" },
                            "inLanguage": "es-UY"
                        },
                        {
                            "@type": "SoftwareApplication",
                            "name": "AgendateHoy",
                            "applicationCategory": "BusinessApplication",
                            "operatingSystem": "Web, iOS, Android",
                            "description": "Plataforma de agendamiento online y gestión de turnos para profesionales en Uruguay.",
                            "url": "https://www.agendatehoy.com/",
                            "offers": [
                                {
                                    "@type": "Offer",
                                    "name": "Plan Básico",
                                    "price": "0",
                                    "priceCurrency": "UYU",
                                    "category": "free"
                                },
                                {
                                    "@type": "Offer",
                                    "name": "Plan Profesional Mensual",
                                    "price": "590",
                                    "priceCurrency": "UYU"
                                },
                                {
                                    "@type": "Offer",
                                    "name": "Plan Profesional Anual",
                                    "price": "5900",
                                    "priceCurrency": "UYU"
                                }
                            ]
                        }
                    ]
                })}</script>
            </Helmet>

            <div className='bg-gradient-to-br from-gray-900 to-blue-900'>
                <NavbarLanding />
                <main>
                    <HeroSection />
                    
                    <Suspense fallback={<div className='h-screen bg-gray-50 dark:bg-gray-900' />}>
                        <LazyFeaturesSection />
                        <LazyHowItWorksSection />
                        <LazyShowcaseSection {...brandingData} />
                        <LazyShowcaseSection {...statsData} />
                        <LazyTestimonialsSection />
                        <LazyPricingSection />
                        <LazyFinalCTASection />
                        <LazyFooter />
                    </Suspense>
                </main>
            </div>
        </>
    );
};

export default HomeLanding;